/**
 * The product portfolio. Every product here is built and owned by Divergent
 * Networks Limited; the site says so on each card and on each product page
 * section, because that ownership is part of what an app-store reviewer reads.
 *
 * `link` is only set for a product whose site is live. Do not add a link to a
 * domain that does not answer: a dead link on this site costs more trust than
 * no link.
 */
export type ProductId = 'veriplus' | 'tambo' | 'sassy';

export interface Product {
  id: ProductId;
  name: string;
  category: string;
  tagline: string;
  summary: string;
  features: { title: string; body: string }[];
  audience: string;
  platforms: string;
  status: string;
  link?: { href: string; label: string };
  accent: string;
}

export const products: readonly Product[] = [
  {
    id: 'veriplus',
    name: 'VeriPlus',
    category: 'Verification as a Service',
    tagline: 'Know that what you hold is genuine.',
    summary:
      'VeriPlus gives manufacturers a structured digital verification workflow. Brands register products and issue serialized codes; consumers scan a code with the VeriPlus app and get a clear verdict on whether the item is genuine.',
    features: [
      {
        title: 'Serialized product codes',
        body: 'Manufacturers register products and issue or ingest unique codes for every unit.',
      },
      {
        title: 'Instant consumer verdicts',
        body: 'A scan returns one of three outcomes (genuine, suspect or unknown) instead of a bare pass/fail.',
      },
      {
        title: 'Full traceability',
        body: 'Every scan is visible to the brand, giving process visibility from the factory to the shelf.',
      },
      {
        title: 'Configurable architecture',
        body: 'A scalable, multi-tenant platform that adapts to each business’s process.',
      },
    ],
    audience: 'Manufacturers, brands and their customers',
    platforms: 'iOS and Android app · Web portal for manufacturers',
    status: 'Manufacturer portal live',
    link: { href: 'https://manufacturer.veri-plus.com', label: 'Manufacturer portal' },
    accent: '#F1560A',
  },
  {
    id: 'tambo',
    name: 'Tambo',
    category: 'Phone Security',
    tagline: 'Confidence in the phone in your pocket.',
    summary:
      'Tambo is a mobile security app that helps people protect their phones and the information on them, with privacy-awareness guidance, real-time security alerts and anti-theft protection.',
    features: [
      {
        title: 'Real-time alerts',
        body: 'Instant notification of potential threats, intrusions and theft events.',
      },
      {
        title: 'Privacy guidance',
        body: 'Clear, practical prompts for managing sensitive information on the device.',
      },
      {
        title: 'User control',
        body: 'People choose their own security preferences; nothing is hidden from them.',
      },
      {
        title: 'Accessible by design',
        body: 'Built to reinforce safer mobile habits for every user, not only experts.',
      },
    ],
    audience: 'Individual phone owners',
    platforms: 'Mobile app',
    status: 'Live',
    link: { href: 'https://tambo-app.com', label: 'tambo-app.com' },
    accent: '#2B37FF',
  },
  {
    id: 'sassy',
    name: 'Sassy',
    category: 'Business Networking',
    tagline: 'Professional connections with purpose.',
    summary:
      'Sassy is a business networking platform for professional growth. It helps professionals and businesses find each other, make their profiles visible to the right people, and build relationships through purposeful engagement.',
    features: [
      {
        title: 'Professional profiles',
        body: 'Profile visibility designed for professional and commercial discovery.',
      },
      {
        title: 'Advanced discovery',
        body: 'Find the people and businesses that matter to your work.',
      },
      {
        title: 'Collaboration tools',
        body: 'Purposeful engagement that turns introductions into working relationships.',
      },
    ],
    audience: 'Professionals and businesses',
    platforms: 'Mobile app',
    status: 'In development',
    accent: '#0A84FF',
  },
];
